'use client';

/**
 * QtyStepper — a fixed-width −/number/+ control that debounces commit
 * calls while the buttons are being clicked.
 *
 * `mcp_line_edit` commits immediately and appends a real ValueState per
 * call, so stepping from 1 to 125 one click at a time would mint 124
 * permanent nodes for a number reached in one motion. Clicks update the
 * optimistic display instantly; only the value after clicks settle
 * (500ms of no further clicking) actually commits. Typing a number
 * directly and blurring is a single write already and bypasses the
 * debounce.
 *
 * Extracted out of `GroupHeaderRow` in `costplan-lines-card.tsx` — the
 * only call site, but self-contained enough (debounce timer + optimistic
 * state) to warrant its own file rather than inlining in the row.
 *
 * @component_role: Primitive
 * @component_type: Control
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useEffect, useRef, useState } from 'react';

export interface QtyStepperProps {
  value: number;
  disabled: boolean;
  testId?: string;
  onCommit: (next: number) => void;
}

export function QtyStepper({ value, disabled, testId, onCommit }: QtyStepperProps) {
  const [pendingQty, setPendingQty] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayQty = pendingQty ?? value;

  useEffect(() => {
    if (pendingQty !== null && value === pendingQty) setPendingQty(null);
  }, [value, pendingQty]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const step = (next: number) => {
    const clamped = Math.max(1, next);
    setPendingQty(clamped);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onCommit(clamped);
    }, 500);
  };

  return (
    // Fixed pill width, sized for 3 digits (999) — every row's control
    // then occupies the same box regardless of how many digits the
    // value has, so the −/+ buttons line up down the column instead of
    // drifting with the right-aligned cell's left edge. Native number-
    // input spin arrows stripped for the same reason: their reserved
    // width isn't consistent either.
    <span className="inline-flex items-center justify-center gap-0.5 w-[88px] bg-muted rounded p-0.5">
      <button
        type="button"
        disabled={disabled || displayQty <= 1}
        onClick={() => step(displayQty - 1)}
        className="h-5 w-5 shrink-0 flex items-center justify-center rounded bg-background hover:bg-accent transition-colors disabled:opacity-40"
      >
        −
      </button>
      <input
        key={`qty-${displayQty}`}
        data-testid={testId}
        type="number"
        min="1"
        step="1"
        disabled={disabled}
        defaultValue={displayQty}
        onBlur={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v) && v >= 1 && v !== displayQty) {
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            setPendingQty(v);
            onCommit(v);
          }
        }}
        className="w-10 text-center tabular-nums bg-transparent border border-transparent hover:border-border focus:border-primary rounded focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => step(displayQty + 1)}
        className="h-5 w-5 shrink-0 flex items-center justify-center rounded bg-background hover:bg-accent transition-colors disabled:opacity-40"
      >
        +
      </button>
    </span>
  );
}
